import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type {
  Subscriber, Audience, Template, Campaign, SendRecord, Suppression,
  SubscriberStatus, CampaignStatus, AudienceRule, Block, TemplateTheme,
} from '@/types/newsletter';

// ─────────── Subscribers ───────────

export type SubscriberFilters = {
  status?: SubscriberStatus;
  audienceId?: string;
  tag?: string;
  search?: string;
  limit?: number;
};

export function useSubscribers(filters: SubscriberFilters = {}) {
  return useQuery({
    queryKey: ['subscribers', filters],
    queryFn: async () => (await api.get<{ items: Subscriber[]; total: number }>(
      '/newsletter/subscribers', { params: filters },
    )).data,
  });
}

export function useSubscriber(id: string | undefined) {
  return useQuery({
    queryKey: ['subscriber', id],
    queryFn: async () => (await api.get<Subscriber>(`/newsletter/subscribers/${id}`)).data,
    enabled: !!id,
  });
}

export type SaveSubscriberInput = Partial<Omit<Subscriber,
  'id' | 'createdAt' | 'updatedAt' | 'totalSends' | 'totalOpens' | 'totalClicks' |
  'lastSentAt' | 'lastOpenedAt' | 'lastClickedAt'
>> & { email: string };

export function useCreateSubscriber() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: SaveSubscriberInput) =>
      (await api.post<{ id: string }>('/newsletter/subscribers', input)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['subscribers'] }),
  });
}

export function useUpdateSubscriber() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...patch }: { id: string } & Partial<SaveSubscriberInput>) =>
      (await api.patch(`/newsletter/subscribers/${id}`, patch)).data,
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['subscribers'] });
      qc.invalidateQueries({ queryKey: ['subscriber', vars.id] });
    },
  });
}

export function useDeleteSubscriber() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await api.delete(`/newsletter/subscribers/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['subscribers'] }),
  });
}

export type CsvImportInput = {
  rows: Array<Record<string, string>>;
  /** Column name mapping: { email: 'Email Address', firstName: 'First Name', ... } */
  mapping: Record<string, string>;
  audienceIds?: string[];
  tags?: string[];
  source?: string;
};

export type CsvImportResult = {
  imported: number;
  updated: number;
  skipped: number;
  invalid: number;
  suppressed: number;
  errors: Array<{ row: number; email?: string; reason: string }>;
};

export function useImportSubscribers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CsvImportInput) =>
      (await api.post<CsvImportResult>('/newsletter/subscribers/import', input)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['subscribers'] });
      qc.invalidateQueries({ queryKey: ['audiences'] });
    },
  });
}

export function useSyncClientsToSubscribers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      (await api.post<{ imported: number; updated: number }>(
        '/newsletter/subscribers/sync-from-clients',
      )).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['subscribers'] }),
  });
}

// ─────────── Audiences ───────────

export function useAudiences() {
  return useQuery({
    queryKey: ['audiences'],
    queryFn: async () =>
      (await api.get<{ items: Audience[] }>('/newsletter/audiences')).data.items,
  });
}

export type SaveAudienceInput = {
  name: string;
  description?: string;
  kind: 'static' | 'dynamic';
  rules?: AudienceRule[];
  color?: string;
};

export function useCreateAudience() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: SaveAudienceInput) =>
      (await api.post<{ id: string }>('/newsletter/audiences', input)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['audiences'] }),
  });
}

export function useUpdateAudience() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...patch }: { id: string } & Partial<SaveAudienceInput>) =>
      (await api.patch(`/newsletter/audiences/${id}`, patch)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['audiences'] }),
  });
}

export function useDeleteAudience() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await api.delete(`/newsletter/audiences/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['audiences'] }),
  });
}

export function useAddToAudience() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ audienceId, subscriberIds }: { audienceId: string; subscriberIds: string[] }) =>
      (await api.post(`/newsletter/audiences/${audienceId}/members`, { subscriberIds })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['audiences'] });
      qc.invalidateQueries({ queryKey: ['subscribers'] });
    },
  });
}

// ─────────── Templates ───────────

export function useTemplates() {
  return useQuery({
    queryKey: ['templates'],
    queryFn: async () =>
      (await api.get<{ items: Template[] }>('/newsletter/templates')).data.items,
  });
}

export function useTemplate(id: string | undefined) {
  return useQuery({
    queryKey: ['template', id],
    queryFn: async () => (await api.get<Template>(`/newsletter/templates/${id}`)).data,
    enabled: !!id,
  });
}

export type SaveTemplateInput = Omit<Template,
  'id' | 'createdAt' | 'createdBy' | 'updatedAt' | 'compiledHtml' | 'compiledText'
>;

export function useCreateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: SaveTemplateInput) =>
      (await api.post<{ id: string }>('/newsletter/templates', input)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['templates'] }),
  });
}

export function useUpdateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...patch }: { id: string } & Partial<SaveTemplateInput>) =>
      (await api.patch(`/newsletter/templates/${id}`, patch)).data,
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['templates'] });
      qc.invalidateQueries({ queryKey: ['template', vars.id] });
    },
  });
}

export function useDeleteTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await api.delete(`/newsletter/templates/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['templates'] }),
  });
}

// ─────────── Campaigns ───────────

export type CampaignFilters = { status?: CampaignStatus; search?: string };

export function useCampaigns(filters: CampaignFilters = {}) {
  return useQuery({
    queryKey: ['campaigns', filters],
    queryFn: async () =>
      (await api.get<{ items: Campaign[] }>('/newsletter/campaigns', { params: filters })).data.items,
  });
}

export function useCampaign(id: string | undefined) {
  return useQuery({
    queryKey: ['campaign', id],
    queryFn: async () => (await api.get<Campaign>(`/newsletter/campaigns/${id}`)).data,
    enabled: !!id,
  });
}

export type SaveCampaignInput = Partial<Omit<Campaign,
  'id' | 'status' | 'createdAt' | 'createdBy' | 'updatedAt' | 'metrics' |
  'sendStartedAt' | 'sendCompletedAt' | 'actualRecipientCount'
>> & {
  name: string;
  subject: string;
  fromName: string;
  fromEmail: string;
  blocks: Block[];
  theme: TemplateTheme;
  audienceIds: string[];
};

export function useCreateCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: SaveCampaignInput) =>
      (await api.post<{ id: string }>('/newsletter/campaigns', input)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['campaigns'] }),
  });
}

export function useUpdateCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...patch }: { id: string } & Partial<SaveCampaignInput>) =>
      (await api.patch(`/newsletter/campaigns/${id}`, patch)).data,
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['campaigns'] });
      qc.invalidateQueries({ queryKey: ['campaign', vars.id] });
    },
  });
}

export function useDeleteCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await api.delete(`/newsletter/campaigns/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['campaigns'] }),
  });
}

export function useSendTestEmail() {
  return useMutation({
    mutationFn: async ({ campaignId, toEmails }: { campaignId: string; toEmails: string[] }) =>
      (await api.post<{ messageId: string }>(
        `/newsletter/campaigns/${campaignId}/test`, { toEmails },
      )).data,
  });
}

export function useScheduleCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id, scheduledFor, timezone,
    }: { id: string; scheduledFor: string; timezone?: string }) =>
      (await api.post(`/newsletter/campaigns/${id}/schedule`, { scheduledFor, timezone })).data,
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['campaigns'] });
      qc.invalidateQueries({ queryKey: ['campaign', vars.id] });
    },
  });
}

export function useSendNow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      (await api.post<{ recipientCount: number }>(
        `/newsletter/campaigns/${id}/send-now`,
      )).data,
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ['campaigns'] });
      qc.invalidateQueries({ queryKey: ['campaign', id] });
    },
  });
}

export function usePauseCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      (await api.post(`/newsletter/campaigns/${id}/pause`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['campaigns'] }),
  });
}

export function useResumeCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      (await api.post(`/newsletter/campaigns/${id}/resume`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['campaigns'] }),
  });
}

export function useCancelCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      (await api.post(`/newsletter/campaigns/${id}/cancel`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['campaigns'] }),
  });
}

export function useEstimateRecipients() {
  return useMutation({
    mutationFn: async ({ audienceIds, excludeAudienceIds }: {
      audienceIds: string[]; excludeAudienceIds?: string[];
    }) =>
      (await api.post<{ count: number }>(
        '/newsletter/campaigns/estimate-recipients',
        { audienceIds, excludeAudienceIds },
      )).data,
  });
}

// ─────────── Sends / Analytics ───────────

export function useCampaignSends(campaignId: string | undefined) {
  return useQuery({
    queryKey: ['campaign-sends', campaignId],
    queryFn: async () =>
      (await api.get<{ items: SendRecord[] }>(
        `/newsletter/campaigns/${campaignId}/sends`,
      )).data.items,
    enabled: !!campaignId,
  });
}

// ─────────── Suppressions ───────────

export function useSuppressions() {
  return useQuery({
    queryKey: ['suppressions'],
    queryFn: async () =>
      (await api.get<{ items: Suppression[] }>('/newsletter/suppressions')).data.items,
  });
}

export function useAddSuppression() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ email, notes }: { email: string; notes?: string }) =>
      (await api.post('/newsletter/suppressions', { email, notes })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['suppressions'] }),
  });
}

export function useRemoveSuppression() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      (await api.delete(`/newsletter/suppressions/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['suppressions'] }),
  });
}
